import { api } from "@/lib/http";

export type GuardrailApp =
  | "SLACK"
  | "SECURE_LLM"
  | "GOOGLE_DRIVE"
  | "EMAIL_DLP"
  | "DESKTOP_OVERLAY"
  | "GATEWAY"
  | (string & {});

export type GuardrailFeatureSettings = {
  toxicity?: {
    id: number;
    name: string;
    description: string;
    settings: {
      threshold: number;
    };
    is_active: boolean;
  };
  language?: {
    id: number;
    name: string;
    description: string;
    settings: {
      min_coverage: number;
      min_confidence: number;
      allowed_languages: Array<string>;
    };
    is_active: boolean;
  };
  topic?: {
    id: number;
    name: string;
    description: string;
    settings: {
      threshold: number;
      disallowed_topics: Array<string>;
    };
    is_active: boolean;
  };
  code?: {
    id: number;
    name: string;
    description: string;
    settings: {
      allowed_languages: Array<string>;
      allow_code_snippets: boolean;
    };
    is_active: boolean;
  };
  sentiment?: {
    id: number;
    name: string;
    description: string;
    settings: {
      negative_threshold: number;
      positive_threshold: number;
    };
    is_active: boolean;
  };
  gibberish?: {
    id: number;
    name: string;
    description: string;
    settings: Record<string, never>;
    is_active: boolean;
  };
};

export type GuardrailFeaturesResponse = {
  source: GuardrailApp;
  settings: GuardrailFeatureSettings;
};

export type UpdateGuardrailSettingsPayload = {
  settings: Record<string, unknown>;
  is_active: boolean;
};

async function getApps() {
  return api.raw.get("api/v1/guardrails/apps").json<Array<GuardrailApp>>();
}

async function getFeatures(app: GuardrailApp) {
  return api.raw
    .get(`api/v1/guardrails/features/${app}`)
    .json<GuardrailFeaturesResponse>();
}

async function updateSettings(
  id: number,
  payload: UpdateGuardrailSettingsPayload,
) {
  return api.raw
    .patch(`api/v1/guardrails/settings/${id}`, {
      json: payload,
    })
    .json();
}

// Analysis API types and functions
export type LanguageAnalysisPayload = {
  allowed_languages: Array<string>;
  min_confidence: number;
  min_coverage: number;
  text: string;
};

export type LanguageDetection = {
  sentence: string;
  language: string;
  language_code: string;
  score: number;
};

export type LanguageAnalysisResponse = {
  detections: Array<LanguageDetection>;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
  primary_language: string;
  primary_language_confidence: number;
  primary_language_coverage: number;
  language_coverages: Record<string, number>;
};

export type ToxicityAnalysisPayload = {
  threshold: number;
  text: string;
};

export type ToxicityAnalysisResponse = {
  label: string;
  score: number;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
};

export type TopicsAnalysisPayload = {
  threshold: number;
  disallowed_topics: Array<string>;
  text: string;
  app_ref: string;
};

export type TopicsAnalysisResponse = {
  flagged_topics: Record<string, number>;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
};

export type CodeAnalysisPayload = {
  allow_code_snippets: boolean;
  allowed_languages: Array<string>;
  text: string;
};

export type CodeSpan = {
  text: string;
  language: string;
  start: number;
  end: number;
};

export type CodeAnalysisResponse = {
  code_spans: Array<CodeSpan>;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
};

export type SentimentAnalysisPayload = {
  negative_threshold: number;
  positive_threshold: number;
  text: string;
};

export type SentimentAnalysisResponse = {
  label: string;
  score: number;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
};

export type GibberishAnalysisPayload = {
  text: string;
};

export type GibberishAnalysisResponse = {
  is_gibberish: boolean;
  violated: boolean;
  violation_details: {
    message: string;
    severity: string;
    details: Record<string, unknown>;
  } | null;
};

async function analyzeLanguage(payload: LanguageAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/language", { json: payload })
    .json<LanguageAnalysisResponse>();
}

async function analyzeToxicity(payload: ToxicityAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/toxicity", { json: payload })
    .json<ToxicityAnalysisResponse>();
}

async function analyzeTopics(payload: TopicsAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/topics", { json: payload })
    .json<TopicsAnalysisResponse>();
}

async function analyzeCode(payload: CodeAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/code", { json: payload })
    .json<CodeAnalysisResponse>();
}

async function analyzeSentiment(payload: SentimentAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/sentiment", { json: payload })
    .json<SentimentAnalysisResponse>();
}

async function analyzeGibberish(payload: GibberishAnalysisPayload) {
  return api.raw
    .post("api/v1/guardrails/gibberish", { json: payload })
    .json<GibberishAnalysisResponse>();
}

const guardrails = {
  getApps,
  getFeatures,
  updateSettings,
  analyzeLanguage,
  analyzeToxicity,
  analyzeTopics,
  analyzeCode,
  analyzeSentiment,
  analyzeGibberish,
};

export default guardrails;
